import React, { useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EMPTY_FORM = {
  title: '',
  description: '',
  location: '',
  startDate: '',
  endDate: '',
  startTime: '',
  endTime: '',
  maxAttendees: '',
  imageUrl: '',
  status: 'published'
};

const EventsManager = ({ club }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [rsvpsLoading, setRsvpsLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    if (!club) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/clubs/${club.id}/events`);
      const data = await res.json();
      if (data.success) setEvents(data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [club]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const openCreate = () => {
    setEditingEvent(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title || '',
      description: event.description || '',
      location: event.location || '',
      startDate: event.startDate ? event.startDate.slice(0, 10) : '',
      endDate: event.endDate ? event.endDate.slice(0, 10) : '',
      startTime: event.startTime || '',
      endTime: event.endTime || '',
      maxAttendees: event.maxAttendees !== null && event.maxAttendees !== undefined ? String(event.maxAttendees) : '',
      imageUrl: event.imageUrl || '',
      status: event.status || 'published'
    });
    setFormError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!form.title || !form.startDate || !form.startTime) {
      setFormError('Title, start date and start time are required.');
      return;
    }
    setFormLoading(true);
    try {
      const payload = {
        ...form,
        clubId: club.id,
        maxAttendees: form.maxAttendees ? parseInt(form.maxAttendees) : null,
        endDate: form.endDate || null,
        endTime: form.endTime || null,
        imageUrl: form.imageUrl || null
      };

      let res;
      if (editingEvent) {
        res = await authService.put(`${API_URL}/events/${editingEvent.id}`, payload);
      } else {
        res = await authService.post(`${API_URL}/events`, payload);
      }

      if (res.success) {
        await fetchEvents();
        setShowForm(false);
        setEditingEvent(null);
      } else {
        setFormError(res.message || 'Failed to save event');
      }
    } catch (err) {
      setFormError(err.message || 'An error occurred');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this event? This will also remove all RSVPs.')) return;
    try {
      await authService.delete(`${API_URL}/events/${eventId}`);
      await fetchEvents();
      if (selectedEvent?.id === eventId) setSelectedEvent(null);
    } catch (err) {
      alert('Failed to delete event');
    }
  };

  const viewRsvps = async (event) => {
    setSelectedEvent(event);
    setRsvpsLoading(true);
    try {
      const res = await authService.get(`${API_URL}/events/${event.id}/rsvps`);
      if (res.success) setRsvps(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setRsvpsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const statusBadge = (status) => {
    const map = {
      published: 'badge-success',
      draft: 'badge-warning',
      cancelled: 'badge-error'
    };
    return map[status] || 'badge-ghost';
  };

  if (!club) {
    return (
      <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
        Select a club to manage its events.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Events for {club.name}</h2>
          <p className="text-sm text-gray-500 mt-1">{events.length} event{events.length !== 1 ? 's' : ''}</p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 text-sm font-medium text-primary-content bg-primary rounded-md hover:bg-primary/90 transition-colors"
        >
          + Create Event
        </button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Events list */}
        <div className={selectedEvent ? 'col-span-6' : 'col-span-12'}>
          {loading ? (
            <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">Loading events…</div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-gray-500">No events yet. Create your first event!</p>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <div
                  key={event.id}
                  className={`bg-white rounded-lg shadow p-5 border-l-4 ${
                    selectedEvent?.id === event.id ? 'border-primary ring-1 ring-primary/20' : 'border-transparent'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="text-base font-semibold text-gray-900">{event.title}</h3>
                        <span className={`badge badge-sm ${statusBadge(event.status)}`}>{event.status}</span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Date:</span>{' '}
                          {formatDate(event.startDate)}
                          {event.endDate && event.endDate !== event.startDate ? ` – ${formatDate(event.endDate)}` : ''}
                        </p>
                        <p>
                          <span className="font-medium">Time:</span> {event.startTime}
                          {event.endTime ? ` – ${event.endTime}` : ''}
                        </p>
                        {event.location && <p><span className="font-medium">Location:</span> {event.location}</p>}
                        <p>
                          <span className="font-medium">RSVPs:</span> {event._count?.rsvps ?? 0}
                          {event.maxAttendees ? ` / ${event.maxAttendees}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-4 flex-shrink-0">
                      <button
                        onClick={() => viewRsvps(event)}
                        className="px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-md hover:bg-primary/20"
                      >
                        View RSVPs
                      </button>
                      <button
                        onClick={() => openEdit(event)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  {event.description && (
                    <p className="text-sm text-gray-500 mt-3 line-clamp-2">{event.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RSVP panel */}
        {selectedEvent && (
          <div className="col-span-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">RSVPs — {selectedEvent.title}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{rsvps.length} attendee{rsvps.length !== 1 ? 's' : ''}</p>
                </div>
                <button
                  onClick={() => { setSelectedEvent(null); setRsvps([]); }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {rsvpsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading…</div>
              ) : rsvps.length === 0 ? (
                <div className="text-center py-8 text-gray-400">No RSVPs yet</div>
              ) : (
                <div className="overflow-auto max-h-[500px]">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left pb-2 font-medium text-gray-600">#</th>
                        <th className="text-left pb-2 font-medium text-gray-600">Name</th>
                        <th className="text-left pb-2 font-medium text-gray-600">Email</th>
                        <th className="text-left pb-2 font-medium text-gray-600">RSVP'd at</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {rsvps.map((rsvp, i) => (
                        <tr key={rsvp.id} className="hover:bg-gray-50">
                          <td className="py-2 text-gray-400">{i + 1}</td>
                          <td className="py-2 font-medium text-gray-900">
                            {rsvp.user.firstName} {rsvp.user.lastName}
                          </td>
                          <td className="py-2 text-gray-600">{rsvp.user.email}</td>
                          <td className="py-2 text-gray-400">
                            {new Date(rsvp.createdAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingEvent ? 'Edit Event' : 'Create New Event'}
              </h2>
              <button
                onClick={() => { setShowForm(false); setEditingEvent(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                  {formError}
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Saturday Game Night"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Tell attendees what to expect…"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={e => setForm({ ...form, location: e.target.value })}
                  placeholder="e.g. 123 Main St, New York"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm({ ...form, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={form.endDate}
                    onChange={e => setForm({ ...form, endDate: e.target.value })}
                    min={form.startDate}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Times */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={e => setForm({ ...form, startTime: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={e => setForm({ ...form, endTime: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              {/* Capacity & Image */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Attendees</label>
                  <input
                    type="number"
                    min="1"
                    value={form.maxAttendees}
                    onChange={e => setForm({ ...form, maxAttendees: e.target.value })}
                    placeholder="Leave blank for unlimited"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form.status}
                    onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (optional)</label>
                <input
                  type="url"
                  value={form.imageUrl}
                  onChange={e => setForm({ ...form, imageUrl: e.target.value })}
                  placeholder="https://…"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 py-2 px-4 text-sm font-medium text-primary-content bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {formLoading ? 'Saving…' : editingEvent ? 'Update Event' : 'Create Event'}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowForm(false); setEditingEvent(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsManager;
